import { useFormFallbackPrices } from '../useFormPrices/useFormFallbackPrices';
import { Control } from 'react-hook-form';
import { IPriceRule } from '../../types/price.types';
import { DELIMITER, NULL } from '../../helpers/priceTable/constants';
import { applyPriceRules, CalculationStep } from '@weareopenr/common/build/src/utilities/pricing';

export interface IGetPlaceholderReturn {
  calculationSteps: CalculationStep[];
  inheritedPlaceholder: string | null;
  calculatedPlaceholder: string | null;
}

export const DEFAULT_PLACEHOLDER = null;

const hasValue = (value: number | null | undefined): value is number => {
  return value !== null && value !== undefined;
};

/***
 * Returns a inherited or calculated placeholder for a price input field based on the field, current form state and applicable price rules.
 * If the input field has a value or is a base price, it returns a default placeholder.
 * If there are applicable price rules, it calculates the placeholder based on those rules.
 * If no applicable rules are found, it inherits the placeholder from another field.
 * @param inputName - The name of the input field in the format "itemId*externalSystemId*priceBandId".
 * @param control - The control object from react-hook-form to access form state.
 * @param priceRules - An array of price rules that may apply to the input field.
 * @return An object containing the calculated placeholder and inherited placeholder.
 */
export const usePlaceholder = (
  inputName: string,
  control: Control,
  priceRules: IPriceRule[],
): IGetPlaceholderReturn => {
  const [itemId, externalSystemId, priceBandId] = inputName.split(DELIMITER);

  const {
    basePrice,
    priceBandDefault,
    externalSystemDefault,
    inputValue: { value_with_tax: inputValue },
  } = useFormFallbackPrices(itemId, externalSystemId, priceBandId, control);
  // if there is a value in the input field or this field is a base price;
  // there is no need to calculate a placeholder or inherit it
  if (hasValue(inputValue) || (externalSystemId === NULL && priceBandId === NULL)) {
    return { calculationSteps: [], calculatedPlaceholder: null, inheritedPlaceholder: DEFAULT_PLACEHOLDER };
  }
  // workout if there are rules applicable to the current field
  // change shape slightly to match the expected shape of rules
  const applicableRules = priceRules?.filter(
    (rule) => rule.price_band?.id === priceBandId || rule.external_system?.id === externalSystemId,
  );
  const formattedRules = applicableRules.map((rule) => ({
    ...rule,
    external_system: rule.external_system?.id ? { id: rule.external_system.id } : null,
    price_band: rule.price_band?.id ? { id: rule.price_band.id } : null,
  }));

  if (applicableRules.length > 0) {
    const prices = [basePrice];

    if (hasValue(priceBandDefault?.value_with_tax)) {
      prices.push(priceBandDefault);
    }
    if (hasValue(externalSystemDefault?.value_with_tax)) {
      prices.push(externalSystemDefault);
    }
    const target = { external_system_id: externalSystemId, price_band_id: priceBandId };
    const calculatedPrices = applyPriceRules(prices, formattedRules, target);

    const currentPrice = calculatedPrices.find(
      (price) => price.external_system_id === externalSystemId && price.price_band_id === priceBandId,
    );
    const calculationSteps = currentPrice?.calculationSteps;
    const calculatedPlaceholder = currentPrice?.value_with_tax;
    if (hasValue(calculatedPlaceholder)) {
      return {
        calculationSteps,
        calculatedPlaceholder: calculatedPlaceholder.toString(),
        inheritedPlaceholder: null,
      };
    }
  }

  // if there are no rules affecting the cell, inherit the placeholder from another field
  const fallbackPrices = [priceBandDefault, externalSystemDefault, basePrice].map((price) => price?.value_with_tax);
  const inheritedPlaceholder = fallbackPrices.find(hasValue);

  return {
    calculationSteps: [],
    calculatedPlaceholder: null,
    inheritedPlaceholder: hasValue(inheritedPlaceholder) ? inheritedPlaceholder.toString() : DEFAULT_PLACEHOLDER,
  };
};
